// ── cohort-assignments-collection: weekly submissions adapter ───────────────

import { useMemo } from "react";
import { Badge, useCollection, type CollectionColumnDef, type UseCollectionResult } from "@vjal/ui";

import type { CoachingAssignmentWeek } from "../../../services/coaching-z6-dtos";
import { COHORT_TAB_PAGE_SIZE } from "./cohort-tab-collection-shared";

export type AssignmentRow = {
  id: string;
  week: string;
  submitted: string;
  matched: string;
  unmatched: string;
} & Record<string, unknown>;

export function mapAssignmentWeeks(weeks: CoachingAssignmentWeek[]): AssignmentRow[] {
  return weeks.map((week) => ({
    id: String(week.weekNumber),
    week: `Week ${week.weekNumber}`,
    submitted: `${week.submittedCount} submitted`,
    matched: `${week.matchedCount} matched`,
    unmatched: week.unmatchedCount > 0 ? `${week.unmatchedCount} unmatched` : "all matched"
  }));
}

export function buildAssignmentsCollectionColumns(): CollectionColumnDef<AssignmentRow>[] {
  return [
    { id: "week", header: "Week", priority: 1, cell: (row) => row.week },
    { id: "submitted", header: "Submitted", priority: 2, cell: (row) => row.submitted },
    { id: "matched", header: "Matched", priority: 2, cell: (row) => row.matched },
    {
      id: "unmatched",
      header: "Unmatched",
      priority: 2,
      cell: (row) => (
        <Badge variant={row.unmatched === "all matched" ? "success" : "warning"}>{row.unmatched}</Badge>
      )
    }
  ];
}

export function useCohortAssignmentsCollection(rows: AssignmentRow[]): UseCollectionResult<AssignmentRow> {
  const columns = useMemo(() => buildAssignmentsCollectionColumns(), []);

  return useCollection<AssignmentRow>({
    mode: "client",
    rows,
    columns,
    pageSize: COHORT_TAB_PAGE_SIZE,
    urlSync: false,
    getRowKey: (row) => row.id
  });
}
